import {
  CreditCard,
  Users,
  MousePointer,
  Plus,
  BarChart3,
  FileText,
  CheckCircle,
  XCircle,
  TrendingUp,
} from 'lucide-react'
import { lazy, Suspense } from 'react'
import { Link } from 'react-router-dom'
import { useLocalization } from '../contexts/LocalizationContext'
import { useDashboardStats, useRecentCards } from '../lib/queries/dashboard'
import {
  Badge,
  buttonStyles,
  Card,
  CardTitle,
  EmptyState,
  PageHeader,
  Skeleton,
  cn,
} from '../components/ui'

const GrowthChart = lazy(() => import('../components/GrowthChart'))
const ActivityChart = lazy(() => import('../components/ActivityChart'))

export default function Dashboard() {
  const { t } = useLocalization()
  const { data: stats, isLoading: statsLoading, isError: statsError } = useDashboardStats()
  const { data: recentCards = [], isLoading: cardsLoading, isError: cardsError } = useRecentCards(5)

  const statTiles = [
    { name: 'Firestore profiles',                 value: stats?.totalUsers,          icon: Users,        accent: false },
    { name: t('DASHBOARD.TOTAL_CARDS'),          value: stats?.totalCards,          icon: CreditCard,   accent: true  },
    { name: t('DASHBOARD.TOTAL_APPLICATIONS'),   value: stats?.totalApplications,   icon: FileText,     accent: false },
    { name: t('DASHBOARD.PENDING_APPLICATIONS'), value: stats?.pendingApplications, icon: MousePointer, accent: false },
  ]

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <PageHeader
        title={t('DASHBOARD.WELCOME_TITLE')}
        subtitle={t('DASHBOARD.WELCOME_SUBTITLE')}
        actions={
          <>
            <Link to="/analytics" className={buttonStyles({ variant: 'secondary', size: 'md' })}>
              <BarChart3 size={16} className="mr-2 -ml-0.5" />
              {t('DASHBOARD.VIEW_ANALYTICS')}
            </Link>
            <Link to="/cards/new" className={buttonStyles({ variant: 'primary', size: 'md' })}>
              <Plus size={16} className="mr-2 -ml-0.5" />
              {t('DASHBOARD.ADD_NEW_CARD')}
            </Link>
          </>
        }
      />

      {/* Stat grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statTiles.map(stat => (
          <Card key={stat.name} accent={stat.accent} className="overflow-hidden">
            <div className="flex items-center gap-4">
              <div
                className={cn(
                  'h-12 w-12 rounded-xl flex items-center justify-center',
                  stat.accent
                    ? 'bg-accent/15 text-accent-deep dark:text-accent-bright'
                    : 'bg-brand/10 text-brand',
                )}
              >
                <stat.icon className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs uppercase tracking-wider font-medium text-ink-muted">
                  {stat.name}
                </p>
                {statsLoading ? (
                  <Skeleton className="mt-2 h-7 w-20" />
                ) : (
                  <p className="font-display text-3xl text-ink tabular-nums">
                    {statsError || stat.value === undefined ? 'Unavailable' : stat.value.toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Suspense fallback={<Card><Skeleton className="h-[280px] w-full" /></Card>}>
        <GrowthChart />
        <ActivityChart />
      </Suspense>

      {/* Recent cards */}
      <Card unpadded>
        <div className="flex items-center justify-between p-6 pb-4">
          <CardTitle>{t('DASHBOARD.RECENT_ACTIVITY')}</CardTitle>
          <Link to="/cards" className={buttonStyles({ variant: 'ghost', size: 'sm' })}>
            View all
          </Link>
        </div>

        {cardsLoading ? (
          <div className="px-6 pb-6 space-y-3">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : cardsError ? (
          <p className="px-6 pb-6 text-sm text-ink-muted">Recent cards are unavailable.</p>
        ) : recentCards.length === 0 ? (
          <EmptyState
            icon={<CreditCard size={24} />}
            title={t('DASHBOARD.NO_CARDS_FOUND')}
            description="Add your first credit card to start tracking activity."
            action={
              <Link to="/cards/new" className={buttonStyles({ variant: 'primary', size: 'md' })}>
                <Plus size={16} className="mr-2 -ml-0.5" />
                {t('DASHBOARD.ADD_NEW_CARD')}
              </Link>
            }
          />
        ) : (
          <ul className="divide-y divide-line">
            {recentCards.map(card => (
              <li
                key={card.id}
                className="flex items-center justify-between gap-3 px-6 py-3.5 hover:bg-surface-elevated/50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-brand-bright to-brand-deep flex items-center justify-center flex-shrink-0">
                    <CreditCard className="h-5 w-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink truncate">{card.name}</p>
                    <p className="text-xs text-ink-subtle">{card.bank}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-medium text-ink tabular-nums">
                    <span className="text-accent">₹</span>
                    {(card.annualFee || 0).toLocaleString('en-IN')}
                  </p>
                  <p className="text-xs text-ink-subtle">{t('CREDIT_CARDS.ANNUAL_FEE')}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Application status + quick stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardTitle className="mb-4">{t('DASHBOARD.APPLICATION_STATUS')}</CardTitle>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-success" />
                <span className="text-sm text-ink-muted">{t('APPLICATIONS.STATUSES.APPROVED')}</span>
              </div>
              <Badge tone="success">
                <span className="tabular-nums">{statsError || !stats ? 'Unavailable' : stats.approvedApplications}</span>
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-error" />
                <span className="text-sm text-ink-muted">{t('APPLICATIONS.STATUSES.REJECTED')}</span>
              </div>
              <Badge tone="error">
                <span className="tabular-nums">{statsError || !stats ? 'Unavailable' : stats.rejectedApplications}</span>
              </Badge>
            </div>
          </div>
        </Card>

        <Card>
          <CardTitle className="mb-4">{t('DASHBOARD.QUICK_STATS')}</CardTitle>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-brand" />
                <span className="text-sm text-ink-muted">{t('DASHBOARD.TOTAL_CARDS')}</span>
              </div>
              <span className="font-display text-lg text-ink tabular-nums">
                {statsError || !stats ? 'Unavailable' : stats.totalCards}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-brand" />
                <span className="text-sm text-ink-muted">Firestore profiles</span>
              </div>
              <span className="font-display text-lg text-ink tabular-nums">
                {statsError || !stats ? 'Unavailable' : stats.totalUsers}
              </span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}
